<template>
  <div v-if="point" class="scrub-readout" :class="`scrub-readout--${anchor}`" :style="floatingStyle">
    <span class="readout-item">
      <span class="material-icons">straighten</span>
      {{ point.distanceLabel }}
    </span>
    <span class="readout-item">
      <span class="material-icons">terrain</span>
      {{ point.elevationLabel }}
    </span>
    <span class="readout-item">
      <span class="readout-swatch" :style="{ backgroundColor: gradeColor }"></span>
      {{ formattedGradient }}
      <span class="readout-bucket">({{ bucketLabel }})</span>
    </span>
    <span v-if="point.surfaceLabel" class="readout-item">
      <span class="readout-swatch" :style="{ backgroundColor: point.surfaceColor || DEFAULT_SURFACE_GROUP_COLOR }"></span>
      {{ point.surfaceLabel }}
    </span>
  </div>
</template>

<script setup lang="ts">
/**
 * Scrub readout tile. Defaults to `anchor="top"` (pinned to the top of the
 * panel) so it's never under a mobile finger — this is a hard requirement,
 * not a style preference, since the touch point during a drag is by
 * definition somewhere over the chart body. `anchor="floating"` (positioned
 * near the scrub x-position instead) is implemented as a distinct,
 * swappable mode per the elevation-panel-v2 plan, for a future desktop-only
 * refinement — it is NOT used on mobile.
 *
 * `distanceLabel`/`elevationLabel` arrive pre-formatted from the container
 * (ElevationPanelV2.vue), NOT raw metres: `ElevationChartData.points[i].y`
 * is already converted to the user's display unit by
 * `buildElevationChartData.ts` (there is no parallel raw-metres elevation
 * array to re-format), so formatting happens once, at the source, rather
 * than risking a double unit-conversion here.
 */
import { computed } from 'vue';
import {
  steepnessBucketForPercent,
  steepnessColorForPercent,
  STEEPNESS_NEUTRAL_COLOR,
} from '../../../utils/elevation/steepnessGradient';
import { DEFAULT_SURFACE_GROUP_COLOR } from '../../../utils/elevation/surfaceGroups';

export interface ScrubPoint {
  distanceLabel: string;
  elevationLabel: string;
  /** Raw signed grade % (unit-independent) — formatted here, and used to look up the bucket label. */
  gradientPercent: number;
  surfaceLabel?: string;
  /** Surface-group colour (matches the chart's line at this point) — swatch shown beside surfaceLabel. */
  surfaceColor?: string;
}

const props = withDefaults(
  defineProps<{
    point: ScrubPoint | null;
    anchor?: 'top' | 'floating';
    /** anchor="floating" only: horizontal position, 0-100. */
    xPercent?: number | null;
  }>(),
  { anchor: 'top', xPercent: null }
);

const formattedGradient = computed(() => {
  if (!props.point) return '';
  const rounded = Math.round(props.point.gradientPercent);
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}%`;
});
const bucketLabel = computed(() => (props.point ? steepnessBucketForPercent(props.point.gradientPercent).label : ''));
// Matches the steepness-fill colour drawn under the chart's line at this point.
const gradeColor = computed(() => (props.point ? steepnessColorForPercent(props.point.gradientPercent) : STEEPNESS_NEUTRAL_COLOR));

const floatingStyle = computed(() => {
  if (props.anchor !== 'floating' || props.xPercent == null) return {};
  const clamped = Math.max(10, Math.min(90, props.xPercent));
  return { left: `${clamped}%`, transform: 'translateX(-50%)' };
});
</script>

<style scoped>
.scrub-readout {
  position: absolute;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  padding: 6px 10px;
  background: rgba(23, 24, 29, 0.95);
  border: 1px solid rgba(172, 176, 191, 0.3);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  font-size: 11px;
  color: #fff;
  pointer-events: none;
  z-index: 5;
  white-space: nowrap;
}

.scrub-readout--top {
  top: 8px;
  right: 8px;
}

.scrub-readout--floating {
  top: 8px;
}

.readout-item {
  display: flex;
  align-items: center;
  gap: 3px;
}

.readout-item .material-icons {
  font-size: 13px;
  color: #ffdf46;
}

/* Grade/surface colour box — matches the steepness fill / surface-group line
   colour drawn on the chart at the scrubbed point, same visual language as a
   Chart.js tooltip's colour swatch. */
.readout-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.readout-bucket {
  color: rgba(255, 255, 255, 0.55);
}
</style>
